import {
  createSelector,
} from 'reselect';

import type {
  Id,
  Resources,
  ResourceType,
  ResourceTypes,
  ResourceMetaType,
  ResourceResourcesType,
} from '../types';

import {
  alphSortByName,
  alphSortByUserName,
} from './utils';

export const getResourceIds = <T extends ResourceType>(
  resourceName: T,
  listName: string = 'all',
) => (state: Resources): Id[] => (
  state[resourceName].lists[listName]
);

export const getResourceMap = <T extends ResourceType>(
  resourceName: T,
) => (
  (state: Resources): ResourceResourcesType<T> => (
    state[resourceName].resources as ResourceResourcesType<T>
  )
);

export const getResourceById = <T extends ResourceType>(
  resourceName: T,
  id: Id,
) => (
  (state: Resources): ResourceTypes[T] => (
    state[resourceName].resources[id] as ResourceTypes[T]
  )
);

export const getResourceMeta = <T extends ResourceType, M extends keyof ResourceMetaType>(
  resourceName: T,
  metaKey: M,
) => (
  (state: Resources): ResourceMetaType[M] => (
    state[resourceName].meta[metaKey]
  )
);

export const getSelectedIds = <T extends ResourceType>(
  resourceName: T,
) => (
  (state: Resources): Id[] => (
    state[resourceName].selectedIds || []
  )
);

export const getRequestProperties = <T extends ResourceType>(
  resourceName: T,
  requestName: string,
) => (
  (state: Resources) => (
    state[resourceName].requests[requestName] || {}
  )
);

export const getResourceLists = <T extends ResourceType>(
  resourceName: T,
  listName?: string,
) => (
  (state: Resources) => (listName
    ? (state[resourceName].lists[listName] || [])
    : state[resourceName].lists)
);

export const getResourceMappedList = <T extends ResourceType>(
  resourceName: T,
  listName: string = 'all',
) => createSelector(
  [
    getResourceIds(resourceName, listName),
    getResourceMap(resourceName),
  ],
  (ids = [], map) => ids.map(id => map[id]),
);

export const getSelectionResourceMappedList = <T extends ResourceType>(
  resourceName: T,
  listName: string = 'all',
) => createSelector(
  [
    getResourceMappedList(resourceName, listName),
    getSelectedIds(resourceName),
  ],
  (resources: ResourceTypes[T][], ids: Id[]) => resources.map(
    (resource) => ({
      ...resource,
      selected: ids.indexOf(resource.id) !== -1,
    }),
  ),
);

export const getSelectedResourceMappedList = <T extends ResourceType>(
  resourceType: T,
  listName: string = 'all',
) => createSelector(
  getSelectionResourceMappedList(resourceType, listName),
  (resources) => resources.filter(resource => resource.selected),
);

export const getResourceByProp = <T extends ResourceType>(
  resourceType: T,
  prop: string,
  value: any,
): (state: Resources) => ResourceTypes[T] | undefined => createSelector(
  getResourceMappedList(resourceType),
  (resources) => resources.find(resource => resource[prop] === value),
);

type ClientResourceType = 'clients' | 'archivedClients' | 'clientGroups';
export function sortedSelectionResourceList<T extends ClientResourceType>(
  resourceType: T,
  listName?: string,
) {
  return createSelector(
    getSelectionResourceMappedList(resourceType, listName),
    (items: Array<ResourceTypes[T] & { selected: boolean }>) => {
      if (resourceType === 'clientGroups') {
        return items.sort(alphSortByName);
      }

      return items
        .filter((item): item is typeof item & { is_pro: boolean } =>
          'is_pro' in item && !item.is_pro,
        )
        .sort(alphSortByUserName);
    },
  );
}
